import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage, tr } from "../i18n";
import { lp } from "../components/Navbar";
import { PageHero } from "./Shared";
import { Reveal, Stagger, StaggerItem, Kicker, SectionHead } from "../components/motion";
const TIERS = [{
  name: "ERI",
  price: "FREE",
  note: "Ethical Readiness Index",
  desc: "For organizations wanting an initial AI readiness assessment.",
  cta: {
    label: "Start ERI",
    to: "/eri"
  },
  testid: "pricing-eri"
}, {
  name: "Single-Entity AI Assessment",
  price: "From €750",
  note: "per entity",
  desc: "For organizations with 1 to 10 entities.",
  cta: {
    label: "Request Quote",
    to: "/contact?service=Single-Entity%20AI%20Assessment"
  },
  testid: "pricing-individual"
}, {
  name: "Group-Wide AI Assessment",
  price: "From €2,500 - €15,000",
  note: "scope, size and complexity based",
  desc: "For organizations with 11+ entities or large and complex organizational structures.",
  cta: {
    label: "Request Quote",
    to: "/contact?service=Group-Wide%20AI%20Assessment"
  },
  testid: "pricing-group"
}, {
  name: "Annual Recertification",
  price: "Custom quotation",
  note: "12-month certification cycle",
  desc: "Annual reassessment to maintain certification.",
  cta: {
    label: "Request Renewal Quote",
    to: "/contact?service=Annual%20Recertification"
  },
  testid: "pricing-recert"
}];
const COMPARISON = [["Entities", "1 to 10", "11+"], ["Large / complex groups", "Not primarily intended", "Yes"], ["Group governance", "Limited", "Included"], ["Entity-level considerations", "Yes", "Yes"], ["AI systems / agents", "Included within scope", "Included within scope"], ["Risk assessment", "Yes", "Yes"], ["Certification", "ESAIA Certificate", "ESAIA Group Certificate"], ["Validity", "12 months", "12 months"], ["Annual reassessment", "Required for renewal", "Required for renewal"], ["Pricing", "From €750 / entity", "From €2,500 - €15,000"]];
const eur = n => "€" + Math.round(n).toLocaleString("en-IE");
function Calculator() {
  const { lang } = useLanguage();
  const [entities, setEntities] = useState(4);
  const [systems, setSystems] = useState(6);
  const [agents, setAgents] = useState(2);
  const [complexity, setComplexity] = useState(1.2);
  const estimate = useMemo(() => {
    let base;
    if (entities <= 10) {
      base = entities * 750 + systems * 120 + agents * 200;
    } else {
      base = 2500 + entities * 350 + systems * 180 + agents * 280;
    }
    base *= complexity;
    return {
      low: Math.max(base * 0.85, entities <= 10 ? 750 : 2500),
      high: base * 1.25,
      track: entities <= 10 ? "Single-Entity AI Assessment" : "Group-Wide AI Assessment"
    };
  }, [entities, systems, agents, complexity]);
  const sliders = [{
    label: "Number of entities",
    value: entities,
    set: setEntities,
    min: 1,
    max: 60,
    testid: "calc-entities"
  }, {
    label: "Number of AI systems",
    value: systems,
    set: setSystems,
    min: 0,
    max: 50,
    testid: "calc-systems"
  }, {
    label: "Number of AI agents",
    value: agents,
    set: setAgents,
    min: 0,
    max: 30,
    testid: "calc-agents"
  }];
  return <div className="border border-esaia-line bg-white" data-testid="pricing-calculator">
      <div className="border-b border-esaia-line p-8">
        <Kicker>{tr("Indicative estimator")}</Kicker>
        <h3 className="mt-4 text-xl font-semibold text-esaia-ink sm:text-2xl">{tr("Estimate your assessment range")}</h3>
        <p className="mt-3 text-sm leading-relaxed text-esaia-muted">{tr("Adjust the parameters below for an indicative range. Final pricing requires an ESAIA quotation.")}{" "}</p>
      </div>
      <div className="grid grid-cols-1 gap-10 p-8 lg:grid-cols-2">
        <div className="space-y-7">
          {sliders.map(s => <div key={s.label}>
              <div className="flex items-center justify-between">
                <label htmlFor={s.testid} className="text-sm font-medium text-esaia-ink">{tr(s.label)}</label>
                <span className="font-mono text-sm text-esaia-blue">{s.value}</span>
              </div>
              <input id={s.testid} type="range" min={s.min} max={s.max} value={s.value} onChange={e => s.set(Number(e.target.value))} className="mt-3 w-full accent-esaia-blue" data-testid="pricing-calculator-slider" />
            </div>)}
          <div>
            <label htmlFor="calc-complexity" className="text-sm font-medium text-esaia-ink">{tr("Organizational complexity")}</label>
            <select id="calc-complexity" value={complexity} onChange={e => setComplexity(Number(e.target.value))} className="mt-3 w-full border border-esaia-line bg-white px-3 py-2.5 text-sm text-esaia-ink focus:border-esaia-blue focus:outline-none" data-testid="calc-complexity">
              <option value={1}>{tr("Standard (single country, centralized)")}</option>
              <option value={1.2}>{tr("Moderate (multiple units or countries)")}</option>
              <option value={1.5}>{tr("High (international group, distributed governance)")}</option>
            </select>
          </div>
        </div>
        <div className="flex flex-col justify-center border border-esaia-line bg-esaia-canvas p-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle">{tr("Recommended track")}</p>
          <p className="mt-2 text-lg font-semibold text-esaia-ink" data-testid="calc-track">{tr(estimate.track)}</p>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle">{tr("Indicative range")}</p>
          <p className="mt-2 font-serif text-3xl font-light text-esaia-blue sm:text-4xl" data-testid="calc-result">
            {eur(estimate.low)} - {eur(estimate.high)}
          </p>
          <p className="mt-5 text-xs leading-relaxed text-esaia-subtle">{tr("Indicative only. Final pricing depends on organizational size, complexity, number of entities, AI systems and agents in scope, geographic structure and overall assessment requirements. Final pricing requires an ESAIA quotation.")}{" "}</p>
          <Link to={lp(lang, "/contact")} data-testid="calc-quote-button" className="group mt-6 inline-flex w-fit items-center gap-2 bg-esaia-blue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark">{tr("Request an exact quote")}{" "}<ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </div>;
}
export default function Pricing() {
  const {
    lang
  } = useLanguage();
  return <div data-testid="pricing-page">
      <PageHero kicker={tr("Pricing")} title={tr("Transparent starting points. Scoped final pricing.")} copy={tr("ESAIA pricing is based on scope, size, complexity and AI environment, not merely the number of legal entities. Use the estimator for an indication, then request a quotation for exact pricing.")} />

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <Stagger className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {TIERS.map(t => <StaggerItem key={t.name}>
              <div className="flex h-full flex-col border border-esaia-line bg-white p-8 transition-all duration-300 hover:border-esaia-blue/50 hover:shadow-md sm:p-9" data-testid={t.testid}>
                <h2 className="text-base font-semibold text-esaia-ink">{tr(t.name)}</h2>
                <p className="mt-4 text-xl font-semibold text-esaia-blue">{tr(t.price)}</p>
                <p className="mt-1 text-xs text-esaia-subtle">{tr(t.note)}</p>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-esaia-muted">{tr(t.desc)}</p>
                <Link to={lp(lang, t.cta.to)} data-testid={`${t.testid}-cta`} className="mt-6 inline-flex items-center justify-center gap-2 border border-esaia-blue/40 px-4 py-2.5 text-sm font-semibold text-esaia-blue transition-colors hover:bg-esaia-blue hover:text-white">
                  {tr(t.cta.label)}
                </Link>
              </div>
            </StaggerItem>)}
        </Stagger>
        <Reveal delay={0.1}>
          <p className="mt-6 text-xs leading-relaxed text-esaia-subtle" data-testid="pricing-global-disclaimer">{tr("All ESAIA certification pricing depends on organizational size, complexity, number of entities, AI systems and agents in scope, and overall assessment requirements.")}{" "}</p>
        </Reveal>

        <div className="mt-16">
          <Calculator />
        </div>
      </section>

      <section className="border-y border-esaia-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <SectionHead kicker={tr("Individual vs Group")} title={tr("Which certification track fits your structure?")} copy={tr("Group certification is designed around one AI environment with one structured assurance framework and group-wide visibility, covering central AI governance, group policies, shared AI infrastructure, agents, systems, entity-level implementation, risk distribution and governance responsibilities.")} />
          <Reveal delay={0.15} className="mt-12 overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse border border-esaia-line bg-white" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-esaia-line bg-esaia-canvas">
                  <th className="p-4 text-left font-mono text-[11px] uppercase tracking-[0.2em] text-esaia-subtle"></th>
                  <th className="p-4 text-left text-sm font-semibold text-esaia-ink">{tr("Single-Entity")}</th>
                  <th className="p-4 text-left text-sm font-semibold text-esaia-blue">{tr("Group-Wide")}</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map(([label, ind, grp]) => <tr key={label} className="border-b border-esaia-line last:border-0">
                    <td className="p-4 text-sm font-medium text-esaia-ink">{tr(label)}</td>
                    <td className="p-4 text-sm text-esaia-muted">{tr(ind)}</td>
                    <td className="p-4 text-sm text-esaia-muted">{tr(grp)}</td>
                  </tr>)}
              </tbody>
            </table>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-4 text-xs text-esaia-subtle">{tr("Final pricing depends on size, complexity and assessment scope.")}{" "}</p>
          </Reveal>
        </div>
      </section>

    </div>;
}
